import React, { useEffect, useState, useRef } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "../../firebaseConfig";
import { toast } from "react-toastify";

import AssignProductToClient from "./AssignProductClient";

// Composant Dropdown personnalisé pour mobile et desktop
const CustomDropdown = ({ value, onChange, options, placeholder, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between"
      >
        <span className="text-gray-900">{selectedOption?.label || placeholder}</span>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[9999] max-h-60 overflow-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                value === option.value ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RevendeurDashboard = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  
  // Options pour les dropdowns
  const filterOptions = [
    { value: "all", label: "Tous les statuts" },
    { value: "pending", label: "En cours" },
    { value: "completed", label: "Terminés" },
    { value: "controls", label: "Contrôles disponibles" }
  ];

  const sortOptions = [
    { value: "recent", label: "Plus récents" },
    { value: "name", label: "Nom A-Z" },
    { value: "controls", label: "Contrôles en priorité" }
  ];
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDossier, setSelectedDossier] = useState(null);
  
  // États pour l'aperçu PDF ultra-moderne
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  
  const db = getFirestore(app);
  const auth = getAuth();

  const fetchDossiers = async () => {
    const user = auth.currentUser;
    if (!user) return console.error("Utilisateur non authentifié");

    try {
      const snapshot = await getDocs(
        query(
          collection(db, "dossiers"),
          where("revendeurEmail", "==", user.email),
          where("destinataire_type", "==", "Revendeur")
        )
      );
      const dossiersFiltres = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });
      setDossiers(dossiersFiltres);
    } catch (error) {
      console.error("Erreur lors de la récupération des dossiers :", error);
      toast.error("Erreur lors du chargement des dossiers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDossiers();
  }, []);

  // Fonctions pour le contrôle périodique
  const getControlePeriodiqueDate = (dossier) => {
    if (!dossier.createdAt) return null;
    
    let createdDate;
    if (dossier.createdAt.toDate && typeof dossier.createdAt.toDate === 'function') {
      createdDate = dossier.createdAt.toDate();
    } else if (dossier.createdAt instanceof Date) {
      createdDate = dossier.createdAt;
    } else {
      createdDate = new Date(dossier.createdAt);
    }
    
    if (isNaN(createdDate.getTime())) return null;
    
    // Ajouter 6 mois à la date de création
    const controleDate = new Date(createdDate);
    controleDate.setMonth(controleDate.getMonth() + 6);
    
    return controleDate;
  };

  const isControleAvailable = (dossier) => {
    const controleDate = getControlePeriodiqueDate(dossier);
    if (!controleDate) return false;
    
    const now = new Date();
    return controleDate <= now;
  };

  const getDaysRemaining = (dossier) => {
    const controleDate = getControlePeriodiqueDate(dossier);
    if (!controleDate) return 0;
    
    const now = new Date();
    const diffTime = controleDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  const formatDate = (date) => {
    if (!date) return 'Non définie';
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      return 'Non définie';
    }
    if (isNaN(dateObj.getTime())) return 'Non définie';
    return dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getControleStatus = (dossier, produit) => {
    const isAvailable = isControleAvailable(dossier);
    const status = produit.controlePeriodiqueStatus || 'pending';
    if (status === 'completed') return 'completed';
    if (isAvailable) return 'available';
    return 'pending';
  };

  // Filtrage et tri
  const filteredDossiers = dossiers.filter(dossier => {
    const matchesSearch = dossier.orderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dossier.produits?.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "pending") return matchesSearch && !dossier.produits?.every(p => p.filled);
    if (filterStatus === "completed") return matchesSearch && dossier.produits?.every(p => p.filled);
    if (filterStatus === "controls") return matchesSearch && isControleAvailable(dossier);
    
    return matchesSearch;
  }).sort((a, b) => {
    if (sortBy === "recent") return new Date(b.createdAt) - new Date(a.createdAt);
    if (sortBy === "name") return a.orderName.localeCompare(b.orderName);
    if (sortBy === "controls") {
      const aControls = isControleAvailable(a);
      const bControls = isControleAvailable(b);
      if (aControls && !bControls) return -1;
      if (!aControls && bControls) return 1;
      return 0;
    }
    return 0;
  });

  // Gestionnaires d'événements
  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce dossier ?")) return;
    try {
      await deleteDoc(doc(db, "dossiers", id));
      toast.success("Dossier supprimé avec succès");
      fetchDossiers();
    } catch (err) {
      console.error("Erreur lors de la suppression du dossier :", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleAssignToClient = (dossier, product) => {
    setSelectedDossier(dossier);
    setSelectedProduct(product);
    setShowAssignModal(true);
  };

  const handleAssignSuccess = () => {
    fetchDossiers();
    toast.success("Produit assigné au client avec succès");
  };

  // Gestionnaire pour l'aperçu PDF
  const handlePdfPreview = (pdfUrl, title, productName) => {
    setCurrentPdf({ url: pdfUrl, title, productName });
    setShowPdfViewer(true);
  };

  const getProductControlLink = (product) => {
    const normalized = (product.name || "")
      .toLowerCase()
      .replace(/fit\s+/g, "")
      .replace(/\s+/g, "-");
    return `/revendeur/controle-periodique/${normalized}`;
  };

  const isProductAssigned = (product) => {
    return product.clientEmail && product.clientEmail.trim() !== '';
  };

  // Stats rapides
  const stats = {
    total: dossiers.length,
    pending: dossiers.filter(d => !d.produits?.every(p => p.filled)).length,
    controlsAvailable: dossiers.filter(d => isControleAvailable(d)).length,
    completed: dossiers.filter(d => d.produits?.every(p => p.filled)).length
  };

  // Composant PDF Viewer
  const ModernPdfViewer = () => {
    if (!showPdfViewer || !currentPdf) return null;

    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm">
        <div className="h-full flex flex-col">
          
          {/* Barre de navigation supérieure */}
          <div className="bg-gray-900/95 backdrop-blur-md border-b border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowPdfViewer(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors group"
                >
                  <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="text-white">
                  <h3 className="font-semibold text-lg">{currentPdf.title}</h3>
                  <p className="text-gray-300 text-sm">{currentPdf.productName}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <a
                  href={currentPdf.url}
                  download
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Télécharger
                </a>
                
                <a
                  href={currentPdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Ouvrir
                </a>
              </div>
            </div>
          </div>
          
          {/* Contenu PDF */}
          <div className="flex-1 relative">
            <iframe
              src={`${currentPdf.url}#view=FitH&navpanes=0&toolbar=1`}
              className="w-full h-full border-0 bg-white"
              title={currentPdf.title}
            />
            
            {/* Overlay de contrôles en bas */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
              <div className="bg-gray-900/90 backdrop-blur-md rounded-xl px-4 py-2 flex items-center space-x-3">
                <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300 hover:text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
                <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300 hover:text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
                <div className="w-px h-6 bg-gray-600"></div>
                <span className="text-gray-300 text-sm font-medium">ESC pour fermer</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Hook pour fermer avec ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showPdfViewer) {
        setShowPdfViewer(false);
      }
    };
    
    if (showPdfViewer) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showPdfViewer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Chargement en cours</h3>
            <p className="text-gray-600">Récupération de vos dossiers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      
      {/* Header avec navigation sticky */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          
          {/* Titre et stats */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Gestion des dossiers CE
              </h1>
              <p className="text-gray-600">
                Suivez l'avancement de vos conformités européennes
              </p>
            </div>
            
            {/* Stats rapides */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
                <div className="text-sm text-gray-600">En cours</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-blue-600">{stats.controlsAvailable}</div>
                <div className="text-sm text-gray-600">Contrôles dispo</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
                <div className="text-sm text-gray-600">Terminés</div>
              </div>
            </div>
          </div>
          
          {/* Barre de recherche et filtres */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                placeholder="Rechercher un dossier ou produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex gap-3">
              <CustomDropdown
                value={filterStatus}
                onChange={setFilterStatus}
                options={filterOptions}
                placeholder="Filtrer par statut"
                className="min-w-[160px]"
              />
              
              <CustomDropdown
                value={sortBy}
                onChange={setSortBy}
                options={sortOptions}
                placeholder="Trier par"
                className="min-w-[140px]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {filteredDossiers.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? "Aucun résultat trouvé" : "Aucun dossier disponible"}
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              {searchTerm 
                ? "Essayez de modifier vos critères de recherche"
                : "Vos dossiers CE apparaîtront ici une fois qu'ils vous seront assignés"
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Effacer la recherche
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredDossiers.map((dossier) => (
              <div
                key={dossier.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
              >
                
                {/* Header du dossier */}
                <div className="relative bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 p-6 border-b border-gray-100">
                  <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h2 className="text-xl font-bold text-gray-900">
                          {dossier.orderName}
                        </h2>
                        {isControleAvailable(dossier) && (
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          <span>Créé le {formatDate(dossier.createdAt)}</span>
                        </div>
                        {dossier.deliveryDate && (
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                            <span>Livraison : {dossier.deliveryDate}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                          <span>
                            Contrôle : {formatDate(getControlePeriodiqueDate(dossier))}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span
                        className={`px-3 py-1.5 text-sm rounded-full font-medium max-w-full truncate ${
                          dossier.status === "en_attente_remplissage"
                            ? "bg-orange-100 text-orange-700 border border-orange-200"
                            : dossier.status === "validé"
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-gray-100 text-gray-700 border border-gray-200"
                        }`}
                        title={dossier.status}
                      >
                        {dossier.status}
                      </span>
                      
                      {isControleAvailable(dossier) ? (
                        <span className="px-3 py-1.5 text-sm rounded-full font-medium bg-green-100 text-green-700 border border-green-200">
                          Contrôles disponibles
                        </span>
                      ) : (
                        <span className="px-3 py-1.5 text-sm rounded-full font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
                          J-{getDaysRemaining(dossier)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section produits */}
                <div className="p-6">
                  {Array.isArray(dossier.produits) && dossier.produits.length > 0 ? (
                    <div className="grid gap-4">
                      {dossier.produits.map((p, i) => {
                        const controleStatus = getControleStatus(dossier, p);
                        const daysRemaining = getDaysRemaining(dossier);
                        
                        return (
                          <div
                            key={(p.uuid || p.productId || p.name) + i}
                            className="relative bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl p-5 border border-gray-100"
                          >
                            <div className="flex flex-col xl:flex-row gap-6">
                              
                              {/* Informations produit */}
                              <div className="flex-1 space-y-3">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h3 className="font-semibold text-lg text-gray-900 mb-1">
                                      {p.name}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      Quantité : {p.quantity || 1}
                                    </p>
                                  </div>
                                  
                                  {/* Indicateur de progression */}
                                  <div className="hidden xl:block">
                                    <div className={`w-3 h-3 rounded-full ${
                                      p.filled ? 'bg-green-500' : 
                                      controleStatus === 'available' ? 'bg-blue-500' : 
                                      'bg-gray-300'
                                    }`}></div>
                                  </div>
                                </div>
                                
                                {/* Badges de statut */}
                                <div className="flex flex-wrap gap-2">
                                  {isProductAssigned(p) && (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                      </svg>
                                      {p.clientData?.nomEntreprise || p.clientEmail}
                                    </span>
                                  )}
                                  
                                  {controleStatus === 'completed' ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                      Contrôle effectué
                                    </span>
                                  ) : controleStatus === 'available' ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200 animate-pulse">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                      </svg>
                                      Contrôle disponible
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      Dans {daysRemaining} jour{daysRemaining > 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Panel d'actions */}
                              <div className="xl:w-80 space-y-3">
                                
                                {/* Remplir le formulaire */}
                                <div className="space-y-2">
                                  <a
                                    href={`/revendeur/orders/${dossier.id}/produits/${
                                      p.uuid || p.productId || encodeURIComponent(p.name?.toLowerCase() || "")
                                    }`}
                                    className={`block w-full text-center px-4 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] ${
                                      p.filled
                                        ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg hover:shadow-xl"
                                        : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg hover:shadow-xl"
                                    }`}
                                  >
                                    {p.filled ? (
                                      <div className="flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Formulaire complété
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                                          <path fillRule="evenodd" d="M6 8a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                        </svg>
                                        Remplir le formulaire
                                      </div>
                                    )}
                                  </a>

                                  {/* Contrôle périodique avec aperçu */}
                                  {controleStatus === 'completed' ? (
                                    <button
                                      disabled
                                      className="w-full bg-gray-100 text-gray-600 px-4 py-3 rounded-xl font-medium cursor-default border border-gray-200"
                                    >
                                      <div className="flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Contrôle effectué
                                      </div>
                                    </button>
                                  ) : (
                                    <a
                                      href={controleStatus === 'available' 
                                        ? getProductControlLink(p) 
                                        : `${getProductControlLink(p)}?preview=true`
                                      }
                                      className={`block w-full text-center px-4 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] ${
                                        controleStatus === 'available'
                                          ? "bg-gradient-to-r from-purple-500 to-pink-600 text-white"
                                          : "bg-gray-100 text-gray-700 border border-gray-200"
                                      }`}
                                    >
                                      <div className="flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          {controleStatus === 'available' ? (
                                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                          ) : (
                                            <>
                                              <path fillRule="evenodd" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" clipRule="evenodd" />
                                              <path fillRule="evenodd" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" clipRule="evenodd" />
                                            </>
                                          )}
                                        </svg>
                                        {controleStatus === 'available' ? 'Contrôle périodique' : 'Aperçu du contrôle périodique'}
                                      </div>
                                    </a>
                                  )}
                                </div>

                                {/* J-183 badge pour le contrôle */}
                                {controleStatus === 'pending' && (
                                  <div className="text-center">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      J-{daysRemaining}
                                    </span>
                                  </div>
                                )}

                                {/* Actions secondaires */}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleAssignToClient(dossier, p)}
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      isProductAssigned(p)
                                        ? "bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-200"
                                        : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border border-indigo-200"
                                    }`}
                                  >
                                    <div className="flex items-center justify-center gap-1">
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                      </svg>
                                      {isProductAssigned(p) ? "Modifier" : "Assigner"}
                                    </div>
                                  </button>
                                </div>

                                {/* Documents avec aperçu */}
                                {(p.documents?.controlePeriodique?.url || p.documents?.declarationCE?.url) && (
                                  <div className="space-y-2">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                      Documents
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      {p.documents?.controlePeriodique?.url && (
                                        <button
                                          onClick={() => handlePdfPreview(
                                            p.documents.controlePeriodique.url,
                                            'Contrôle périodique',
                                            p.name
                                          )}
                                          className="group bg-white border border-gray-200 hover:border-gray-300 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-md"
                                        >
                                          <div className="flex items-center justify-center gap-1">
                                            <svg className="w-3 h-3 text-gray-500 group-hover:text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                            </svg>
                                            Contrôle
                                          </div>
                                        </button>
                                      )}

                                      {p.documents?.declarationCE?.url && (
                                        <button
                                          onClick={() => handlePdfPreview(
                                            p.documents.declarationCE.url,
                                            'Déclaration CE',
                                            p.name
                                          )}
                                          className="group bg-white border border-gray-200 hover:border-gray-300 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-md"
                                        >
                                          <div className="flex items-center justify-center gap-1">
                                            <svg className="w-3 h-3 text-gray-500 group-hover:text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                            </svg>
                                            Déclaration
                                          </div>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-red-50 rounded-xl border border-red-200">
                      <div className="text-red-400 text-4xl mb-3">⚠️</div>
                      <p className="text-red-700 font-medium">
                        Aucun produit associé à ce dossier
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer du dossier */}
                <div className="bg-gray-50/50 px-6 py-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://drive.google.com/drive/u/0/folders/1SkwJS3TckM34AMIVnZ5QW8zV-R6oN5yK"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 text-sm transition-colors shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                      </svg>
                      Notice d'instruction
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal d'assignation client */}
        <AssignProductToClient
          dossier={selectedDossier}
          produit={selectedProduct}
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onSuccess={handleAssignSuccess}
        />

        {/* PDF Viewer */}
        <ModernPdfViewer />
      </div>
    </div>
  );
};

export default RevendeurDashboard;